/**
 * @typedef {Object} ManagedFormArgs
 * @property {string} formRef
 * @property {string[]} [allowlistedInputs]
 */

/**
 * @typedef {Object} ManagedForm
 * @property {string} formRef
 * @property {string[]} allowlistedInputs
 * @property {boolean} showSubmitWarning
 * @property {boolean} confirmSubmit
 * @property {boolean} isSubmitting
 * @property {Object.<string, any>} fields
 * @property {boolean} isDirty
 * @property {Array} warningMessages
 * @property {Array} destructiveMessages
 * @property {function(): void} init
 * @property {function(): void} destroy
 * @property {function(Object): void} register
 * @property {function(string, Object): void} updateFieldConfig
 * @property {function(string): string} nameToKey
 * @property {function(string): function} watch
 * @property {function(Event): void} onSubmit
 * @property {function(Event): void} onBeforeUnload
 * @property {function(Event): void} beforeVisitConfirmation
 * @property {function(): Array} dirtyFields
 * @property {function(): void} computeWarningMessages
 * @property {function(): void} computeDestructiveMessages
 * @property {function(): void} addBeforeUnloadWarning
 * @property {function(): void} removeBeforeUnloadWarning
 * @property {function(): void} onConfirmSubmit
 */

/**
 * @typedef ManagedFormChild
 * @property {function(): void} [initChild]
 * @property {function(): void} [destroyChild]
 */

/**
 * @template {ManagedFormChild} T
 * @param {ManagedFormArgs} args
 * @param {T} form
 * @returns {ManagedForm & T & {[key: string]: any}}
 */
export function withManagedForm({ formRef, allowlistedInputs }, form) {
  return Object.assign(managedForm({ formRef, allowlistedInputs }), form);
}

/**
 * @param {ManagedFormArgs} args
 * @returns {ManagedForm}
 */
export function managedForm({ formRef, allowlistedInputs = [] }) {
  return {
    formRef,
    allowlistedInputs,
    showSubmitWarning: false,
    confirmSubmit: false,
    isSubmitting: false,
    fields: {},
    isDirty: false,
    warningMessages: [],
    destructiveMessages: [],
    init() {
      if (typeof this.initChild === "function") {
        this.initChild();
      }
      this.$nextTick(() => {
        const elements = this.$refs[formRef].elements;
        for (const el of elements) {
          if (
            !el.name ||
            (el.type === "hidden" && !this.allowlistedInputs.includes(el.name))
          ) {
            continue;
          }
          this.register({ $el: el });
        }
      });
    },
    destroy() {
      if (typeof this.destroyChild === "function") {
        this.destroyChild();
      }
      this.removeBeforeUnloadWarning();
    },
    register({ $el, type, message, comparator } = {}) {
      /*
      Used to register form components to watch for destructive or warning changes. If any of the registered
      components are changed, the confirmation modal will trigger with the message provided
      in the registration call.

      It will automatically use the element passed to it, or you can register an input using a reference.

      Example:

      ```
      <input type="text" name="destructive-action" x-init="register({type: 'destructive', message: 'Do you really want to take this action?'})" />
      ```
       */
      let initialValue = null;
      const el = $el || this.$el;
      const key = this.nameToKey(el.name);
      switch (el.type) {
        case "checkbox": {
          initialValue = el.checked;
          break;
        }
        default: {
          initialValue = el.value;
        }
      }
      this.updateFieldConfig(key, {
        type,
        message,
        initialValue,
        changed: false,
        comparator,
      });
      this[key] = initialValue;
      // If the form element contains a model binding, we can't override it or it won't work in the existing form.
      if (el.hasAttribute("x-model") && el.getAttribute("x-model") !== key) {
        const xModelKey = el.getAttribute("x-model");
        this[xModelKey] = null;
        this.$watch(xModelKey, this.watch(xModelKey));
        this.$watch(xModelKey, this.watch(key));
      } else {
        el.setAttribute("x-model", key);
        this.$watch(key, this.watch(key));
      }
    },
    updateFieldConfig(key, config) {
      if (!this.fields[key]) {
        this.fields[key] = {};
      }
      for (const [k, v] of Object.entries(config)) {
        if (typeof v !== "undefined") {
          this.fields[key][k] = v;
        }
      }
    },
    nameToKey(name) {
      return `${name}`.replace(/([[\]])/g, "_");
    },
    watch(key) {
      return (value) => {
        if (!this.fields[key]) {
          this.fields[key] = {};
        }
        this.fields[key].changed = value !== this.fields[key].initialValue;

        if (this.dirtyFields().length > 0) {
          this.isDirty = true;
          this.addBeforeUnloadWarning();
        } else {
          this.isDirty = false;
          this.removeBeforeUnloadWarning();
        }
        this.computeDestructiveMessages();
        this.computeWarningMessages();
      };
    },

    onSubmit(event) {
      if (
        (this.warningMessages.length > 0 ||
          this.destructiveMessages.length > 0) &&
        !this.confirmSubmit
      ) {
        event.preventDefault();
        this.$nextTick(() => {
          this.showSubmitWarning = true;
        });
      } else {
        this.removeBeforeUnloadWarning();
      }
    },

    onBeforeUnload(event) {
      if (event.type === "beforeunload") {
        event.preventDefault();
        event.returnValue = true;
      }
      if (event.type === "turbo:before-visit") {
        this.beforeVisitConfirmation();
      }
    },

    beforeVisitConfirmation(event) {
      const leave = window.confirm(
        "You have unsaved changes. If you navigate away, all changes will be lost.",
      );
      if (!leave) {
        event.preventDefault();
      }
    },

    dirtyFields() {
      return Object.entries(this.fields)
        .map(([key, value]) => ({
          key,
          currentValue: this[key],
          ...value,
        }))
        .filter((f) => f.changed);
    },

    computeWarningMessages() {
      this.warningMessages = this.dirtyFields().filter(
        (f) => f.type === "warning",
      );
    },

    computeDestructiveMessages() {
      /* Only show a warning when there are actually destructive changes on a destructive field.
         For instance, if we add a custom field, it's not destructive, but if we remove it, it's
         destructive. Similarly for checkboxes, if the initial value was true and we changed it to false,
         Then we are going to be removing something.
       */
      this.destructiveMessages = this.dirtyFields()
        .filter((f) => f.type === "destructive")
        .filter((f) =>
          typeof f.comparator === "function"
            ? f.comparator(f.initialValue, f.currentValue)
            : f.initialValue === true || typeof f.initialValue !== "boolean",
        );
    },

    addBeforeUnloadWarning() {
      window.addEventListener("beforeunload", this.onBeforeUnload);
      document.addEventListener("turbo:before-visit", this.onBeforeUnload);
    },

    removeBeforeUnloadWarning() {
      window.removeEventListener("beforeunload", this.onBeforeUnload);
      document.removeEventListener("turbo:before-visit", this.onBeforeUnload);
    },

    onConfirmSubmit() {
      this.removeBeforeUnloadWarning();
      this.confirmSubmit = true;
      this.$refs[this.formRef].submit();
    },
  };
}
